import { ComponentChild } from 'preact'
import ButtonControl from '../../preact-components/button-control'
import Flag from '../ui-components/Flag'
import BetaBadge from '../ui-components/BetaBadge'
import OverviewSwitcher from '../ui-components/OverviewSwitcher'
import { common } from '../../preact-components/common'
import TitleInput from '../../preact-components/title-input/TitleInput'
import StatusCircle from '../ui-components/StatusCircle'
import Icon from '../../preact-components/assets'
import classNames from 'classnames'
import StatusBadge from '../ui-components/StatusBadge'

const reloadPage = () => {
  window.location.reload()
}

export interface CustomButton {
  title: string
  variant: string
  url?: string
  newStyle?: boolean
  customClass?: string
  nodeName?: string
  action?: Function | string
}

type SaveProps =
  | {
      saveCallback?: undefined
      disableSaveButton: true
    }
  | {
      saveCallback?: () => void
      disableSaveButton?: false
    }

interface Props {
  cancelCallback?: Function
  isLoading?: boolean
  hasBreadcrumb?: boolean
  breadcrumbLink?: string
  breadcrumbText?: string
  disableCancelButton?: boolean
  customButtons?: Array<CustomButton | ComponentChild>
  statusLabelText?: string
  statusLabelSuccess?: boolean
  statusLabelSecondary?: boolean
  customComponent?: JSX.Element
  isBeta?: boolean
  placeholder?: string
  statusVariant?: string
  status?: string
  onTitleChanged?: Function
  title: string | ComponentChild
  statusBadge?: boolean
}

function isComponent(item: CustomButton | ComponentChild): item is ComponentChild {
  return typeof item === 'object' && item !== null && 'nodeName' in item
}

export const TitleWithExternalLink = (props: { title: string; link: string }) => {
  return (
    <span style='display:flex'>
      {props.title}
      <a href={props.link} style="padding-top:0.1em;margin-left: 4px" target='_blank' rel='noreferrer'>
        <Icon icon='externalLink' height={22} width={22} color='#9599A2' />
      </a>
    </span>
  )
}
const Header = ({
  title,
  saveCallback = () => {},
  cancelCallback = reloadPage,
  isLoading = false,
  hasBreadcrumb = false,
  breadcrumbLink,
  breadcrumbText,
  disableCancelButton = false,
  disableSaveButton = false,
  customButtons = [],
  statusLabelText = undefined,
  statusLabelSuccess = false,
  statusLabelSecondary = false,
  customComponent = undefined,
  isBeta = false,
  onTitleChanged,
  placeholder,
  statusVariant,
  status,
  statusBadge
}: Props & SaveProps) => {
  const headerClass = classNames(
    'control-header',
    hasBreadcrumb && 'control-header--breadcrumb',
    onTitleChanged && 'control-header--title-input'
  )

  return (
    <div className={headerClass}>
      <div className='control-header__texts-container'>
        {hasBreadcrumb && (
          <span className='control-header__breadcrumb'>
            <a className='control-header__breadcrumb-link' href={breadcrumbLink}>
              {breadcrumbText}
            </a>
          </span>
        )}
        {status && !statusBadge && (
          <div className='control-header__status'>
            <StatusCircle variant={statusVariant} />
            {status}
          </div>
        )}
        {onTitleChanged && typeof title === 'string' ? (
          <TitleInput title={title} onTitleChanged={onTitleChanged} placeholder={placeholder} />
        ) : (
          <div className='control-header__title'>
            <h2>{title}</h2>
            {common.isModuleActive('moderationOverviewBetaDefault') &&
              window.location.pathname === '/moderation/overview' &&
              window.location.search === '' && <OverviewSwitcher />}
            {statusLabelText && (
              <Flag text={statusLabelText} success={statusLabelSuccess} secondary={statusLabelSecondary} />
            )}
            {statusBadge && <StatusBadge variant={status}>{status ?? ''}</StatusBadge>}
            {isBeta && <BetaBadge />}
          </div>
        )}
      </div>
      <div className='control-header__buttons'>
        {!disableCancelButton && (
          <ButtonControl title='Cancel' onClick={() => cancelCallback()} newStyle className='js-cancel' size='medium' />
        )}

        {!disableSaveButton && (
          <ButtonControl
            variant='success'
            title='Save Changes'
            onClick={() => saveCallback()}
            disabled={isLoading}
            isLoading={isLoading}
            newStyle
            className='js-submit'
            size='medium'
          />
        )}
        {customButtons
          ? customButtons.map((item, idx) => {
              if (!item) return null
              if (isComponent(item)) return item
              const { url, customClass, variant, title, action, newStyle, size } = item
              if (!action && !url) return null

              return (
                <a className='control-header__button-wrapper' href={url} key={idx}>
                  <ButtonControl
                    newStyle={newStyle}
                    variant={variant}
                    className={customClass}
                    title={title}
                    onClick={action}
                    size={size}
                  />
                </a>
              )
            })
          : null}
      </div>
      {customComponent && customComponent}
    </div>
  )
}

export default Header
